import React, { useEffect, useRef, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Button, Fade, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";

import { useAlert } from "../../contexts/AlertContext";

function replaceTabsWithSpace(input: string): string {
  return input.replace(/\t/g, "  ");
}

type AdServerDetailsProps = {
  request?: string | null | undefined;
  response?: string | null | undefined;
};

const AdServerDetails: React.FC<AdServerDetailsProps> = ({ request, response }) => {
  const [showDetails, setShowDetails] = useState(false);
  const { alert } = useAlert();
  const detailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showDetails && detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showDetails]);

  const handleCopy = async () => {
    if (!request) return;
    try {
      await navigator.clipboard.writeText(request);
      alert.success("Copied to clipboard.");
    } catch (err) {
      alert.error(`Failed to copy to clipboard: ${err}`);
    }
  };

  if (!request && !response) return null;

  return (
    <>
      <Button sx={{ width: "180px" }} variant="contained" onClick={() => setShowDetails(!showDetails)}>
        Ad Server Details
      </Button>
      <Fade in={showDetails} unmountOnExit>
        <Box
          ref={detailsRef}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="h6">Ad Server Request</Typography>
            <IconButton size="small" onClick={handleCopy} data-testid={"copy-button"}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ padding: 2 }}>
            <Box
              component="pre"
              sx={{
                overflowX: "auto",
                fontSize: "14px",
                margin: 0,
              }}
              data-testid={"ad-server-request-payload"}
            >
              {request ? replaceTabsWithSpace(request) : ""}
            </Box>
          </Box>
          <Box
            sx={{
              padding: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
            data-testid={"ad-server-response"}
          >
            <Typography fontSize={"14px"}>Response: {response}</Typography>
          </Box>
        </Box>
      </Fade>
    </>
  );
};

export default AdServerDetails;
